/**
 * ATL-1954 — the Supabase mirror of a working-hours record, built from the SAME object
 * `saveWorkingHours` writes to Firestore.
 *
 * WHY THIS EXISTS
 *
 * `saveWorkingHours` stores the whole record in Firestore (`batch.set(ref, {...info})`) but its
 * dual-write was a hand-written literal of nine keys. `startDate`, `endDate`, `roleDetails`,
 * `dailyStatID`, `monthlyStatID` and `employeeBonusAmount` were never among them, so every record
 * the app created reached Supabase without them. For a MONTHLY salary that is the period itself:
 * `start_date`/`end_date` NULL. On an organization that reads from Supabase, `SalaryScreen` renders
 * a monthly record with `item.startDate.toDate()`, which throws on that null during render, and with
 * no error boundary the whole app goes white. Measured on UNDERWOOD (52e9d1c5-…) on 2026-10-31, the
 * first month end after its 2026-10-25 read cutover: four accruals, four white screens. The bonus
 * rules (`countEmployeeBonuses`) read `roleDetails.roleBonusType` / `individualSalesPercent` from the
 * same records, so a NULL `role_details` silently drops a percent bonus on a Supabase-read org.
 *
 * The reverse-mirror contract had already recorded the gap (mirror-field-contract.json,
 * "roleDetails (NULL rows)", "startDate/endDate (NULL on month rows)", proposedFix "Send them in the
 * dual-write, persist, backfill"). This is the send half.
 *
 * Dates are sent as ISO strings. The DTO validates them with `@IsDateString()`, and a 400 on a
 * dual-write is a deterministic failure the outbox never retries (ATL-782): forwarding a raw
 * Firestore Timestamp would turn a silently-absent field into a permanently lost record.
 */

/** A Firestore Timestamp, or the API client's TimestampLike — anything with `toDate()`. */
interface HasToDate {
  toDate: () => Date
}

export type DualWriteDate = Date | HasToDate | string | null | undefined

/**
 * The time-of-day value a shift stores, `{ value, text }` (legacy records hold a bare number).
 * Type aliases, not interfaces: the API client's request schemas are zod `passthrough()` objects,
 * whose types carry an index signature an interface is not assignable to.
 */
export type HourValueDto = {
  value: number
  text?: string
}

/** The fields of a working-hours record the mirror is built from (a subset of `WorkingHoursInfo`). */
export interface WorkingHoursMirrorSource {
  employeeID?: string
  store?: string
  role?: string
  roleDetails?: object | null
  salaryType?: 'hour' | 'day' | 'month'
  startHour?: unknown
  endHour?: unknown
  amount: number
  date: DualWriteDate
  startDate?: DualWriteDate
  endDate?: DualWriteDate
  dailyStatID?: string
  monthlyStatID?: string
  employeeBonusAmount?: number
}

/** The body `POST /working-hours` and `PATCH /working-hours/firestore/:id` accept. */
export type WorkingHoursMirrorPayload = {
  storeId?: string
  /** Absent when the record names no employee; the create call site sends `''`, as it always has. */
  employeeId?: string
  role?: string
  roleDetails?: Record<string, unknown>
  salaryType?: 'hour' | 'day' | 'month'
  startHour?: HourValueDto
  endHour?: HourValueDto
  amount: number
  date?: string
  startDate?: string
  endDate?: string
  dailyStatId?: string
  monthlyStatId?: string
  employeeBonusAmount?: number
}

const hasToDate = (v: unknown): v is HasToDate =>
  typeof v === 'object' && v !== null && typeof (v as { toDate?: unknown }).toDate === 'function'

/**
 * An instant as an ISO string, or `undefined` when there is none. A value that is present but is
 * not an instant throws: sending it would be a 400 that loses the record, and dropping it silently
 * is the defect this module exists to end.
 */
export const toIsoInstant = (v: DualWriteDate, field: string): string | undefined => {
  if (v === null || v === undefined) return undefined
  const d = hasToDate(v) ? v.toDate() : v instanceof Date ? v : new Date(v)
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) {
    throw new Error(`working-hours dual-write: ${field} is not a valid instant (${String(v)})`)
  }
  return d.toISOString()
}

/**
 * Send the READ HourValue shape `{value, text}` (symmetric transform, ATL-538): the Supabase row
 * must match Firestore's stored `{value, text}` for compare parity, and a legacy raw number must
 * never reach the API as a primitive (the schema expects an object — Sentry 24D/24V "startHour:
 * Expected object, received number").
 */
export const toHourDto = (h: unknown): HourValueDto | undefined => {
  if (h == null) return undefined
  if (typeof h === 'number') return { value: h } // legacy raw number
  if (typeof h === 'object' && (h as { value?: unknown }).value !== undefined) {
    const hv = h as { value: number; text?: string }
    return { value: hv.value, ...(hv.text !== undefined && { text: hv.text }) }
  }
  return undefined // never pass a primitive/unknown shape through
}

/**
 * The role snapshot as a plain object. It is stored whole, exactly as the migration stores
 * `data.roleDetails` (`MigrationService.transformWorkingHour`), because the bonus rules read
 * fields of it (`roleBonusType`, `individualSalesPercent`, `minIndividualSales`, `salaryType`) and
 * ShiftScreen reads `roleDetails.salary`.
 */
const toRoleSnapshot = (roleDetails: object | null | undefined): Record<string, unknown> | undefined =>
  roleDetails && typeof roleDetails === 'object' ? { ...(roleDetails as Record<string, unknown>) } : undefined

/**
 * The mirror of `info`, carrying every field Firestore stores that has a Supabase column. Keys
 * whose value is absent are left out, so an update never clears a column it was not given.
 */
export const workingHoursMirrorPayload = (info: WorkingHoursMirrorSource): WorkingHoursMirrorPayload => {
  const payload: WorkingHoursMirrorPayload = {
    storeId: info.store,
    employeeId: info.employeeID || undefined,
    role: info.role,
    roleDetails: toRoleSnapshot(info.roleDetails),
    salaryType: info.salaryType,
    startHour: toHourDto(info.startHour),
    endHour: toHourDto(info.endHour),
    amount: info.amount,
    date: toIsoInstant(info.date, 'date'),
    startDate: toIsoInstant(info.startDate, 'startDate'),
    endDate: toIsoInstant(info.endDate, 'endDate'),
    dailyStatId: info.dailyStatID,
    monthlyStatId: info.monthlyStatID,
    employeeBonusAmount: typeof info.employeeBonusAmount === 'number' ? info.employeeBonusAmount : undefined,
  }
  for (const key of Object.keys(payload) as (keyof WorkingHoursMirrorPayload)[]) {
    if (payload[key] === undefined) delete payload[key]
  }
  return payload
}
